import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import ChecklistService from '../../../../../../AppSettings/services/ChecklistService';
import ToastService from '../../../../../../AppSettings/services/ToastService';
import './AddDC.css';
import FormEntryMultilineTextbox from '@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox';
import { ChecklistFormData } from '@frontend/Models/Checklist';
import { ChecklistItem } from '@frontend/Models/Checklist';

interface AddDCProps {
  onClose: () => void;
  onItemCreated: (item: ChecklistItem) => void;
}

const AddDC: React.FC<AddDCProps> = ({ onClose, onItemCreated }) => {
  const { matterId } = useParams<{ matterId: string }>();

  const [checklist, setChecklist] = useState<ChecklistFormData>({
    description: '',
    status: 'Pending',
    matterid: matterId || null,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ): void => {
    const { name, value } = e.target;
    setChecklist((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCreation = async (): Promise<void> => {
    setLoading(true);
    setError('');

    try {
      const response = await ChecklistService.addChecklistItem(
        checklist
      );

      if (!response.checklistid) {
        throw new Error(response.message || 'Failed to create item');
      }

      ToastService.success('Checklist item created successfully!');

      const createdItem = {
        ...checklist,
        checklistid: (response as any).checklistid,
        status: checklist.status === 'Completed',
        duedate: null,
      } as ChecklistItem;

      onItemCreated(createdItem);

      setChecklist({
        description: '',
        status: 'Pending',
        matterid: matterId || null,
      });
    } catch (error) {
      console.error('Error creating item:', error);
      setError('Failed to create the checklist item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Close modal when clicking on overlay (but not on modal content)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="add-checklist-overlay"
      onClick={handleOverlayClick}
    >
      <div className="add-checklist-modal">
        {/* Header */}
        <div className="add-checklist-header">
          <h2 className="add-checklist-title">
            Create Checklist Item
          </h2>
          <button
            onClick={onClose}
            className="add-checklist-close-btn"
          >
            <FaTimes />
          </button>
        </div>

        {/* Form Content */}
        <div className="add-checklist-form-container">
          <div className="add-checklist-input-group">
            <label className="add-checklist-label">
              Description
            </label>
            <FormEntryMultilineTextbox
              name="description"
              placeholder="Enter checklist description"
              value={checklist.description}
              onChange={handleInputChange}
              className="add-checklist-textarea"
              rows={3}
            />
          </div>

          <div className="add-checklist-input-group">
            <label className="add-checklist-label">Status</label>
            <select
              name="status"
              value={checklist.status}
              onChange={handleInputChange}
              className="add-checklist-select"
            >
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Error and Loading States */}
        {error && (
          <div className="add-checklist-error-message">{error}</div>
        )}
        {loading && (
          <div className="add-checklist-loading-message">
            Processing...
          </div>
        )}

        {/* Actions */}
        <div className="add-checklist-actions">
          <button
            type="button"
            onClick={onClose}
            className="add-checklist-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="add-checklist-submit-btn"
            onClick={handleCreation}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddDC;
